// "Yeni sürüm var" kartı (8.6 · D-667).
//
// Furkan'ın Android'i üç hafta eski derlemede kaldı ve kimse bilmedi. Taslak
// onaylandı ("Uygundur"): gelen kutusunun üstünde, Bildirimler kartı
// biçiminde bir kart; "Şimdi değil" bir gün susturur; uygulamayı engellemez.
//
// Sınırı: bu kod yalnız onu taşıyan ikilide çalışır. OTA'yla 1.0.8'e ulaşır;
// daha eski bir runtime'daki uygulama (Furkan'ın 1.0.6'sı) bunu hiç almaz.
import { useCallback, useEffect, useState } from 'react';
import { AppState, Linking, Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { api } from '../services/api';
import { installedAppVersion } from './installed';

const SNOOZE_KEY = 'version_notice_snoozed_until';
export const SNOOZE_MS = 24 * 60 * 60 * 1000;
// Aynı oturumda sunucuya en çok saatte bir sorulur.
const CHECK_INTERVAL_MS = 60 * 60 * 1000;

/** "1.0.10" > "1.0.9": parçalar sayı olarak karşılaştırılır. */
export function compareVersions(a: string, b: string): number {
  const pa = a.split('.').map(Number);
  const pb = b.split('.').map(Number);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] ?? 0) - (pb[i] ?? 0);
    if (diff !== 0) return diff < 0 ? -1 : 1;
  }
  return 0;
}

type Release = { latestVersion: string; installUrl: string };
let cache: { at: number; release: Release | null } | null = null;

/** Testler için: önbelleği boşalt. */
export function resetVersionNoticeCache(): void {
  cache = null;
}

async function latestRelease(platform: 'ios' | 'android'): Promise<Release | null> {
  if (cache && Date.now() - cache.at < CHECK_INTERVAL_MS) return cache.release;
  try {
    const body = await api.getLatestRelease(platform);
    const release = typeof body?.latest_version === 'string' && /^https:\/\//.test(body?.install_url ?? '')
      ? { latestVersion: body.latest_version, installUrl: body.install_url }
      : null;
    cache = { at: Date.now(), release };
    return release;
  } catch {
    // Ağ yoksa kart çıkmaz; bir sonraki açılış yeniden sorar.
    return null;
  }
}

async function snoozedUntil(): Promise<number> {
  try {
    return Number(await AsyncStorage.getItem(SNOOZE_KEY)) || 0;
  } catch {
    return 0;
  }
}

export function useVersionNotice() {
  const [installUrl, setInstallUrl] = useState<string | null>(null);

  const check = useCallback(async () => {
    if (Platform.OS !== 'ios' && Platform.OS !== 'android') return;
    const installed = installedAppVersion();
    if (!installed) return;
    const release = await latestRelease(Platform.OS);
    if (!release || compareVersions(installed, release.latestVersion) >= 0) {
      setInstallUrl(null);
      return;
    }
    if ((await snoozedUntil()) > Date.now()) return;
    setInstallUrl(release.installUrl);
  }, []);

  useEffect(() => {
    check();
    // Uygulama öne gelince de bakılır (saatlik önbellekle).
    const sub = AppState.addEventListener('change', (state) => {
      if (state === 'active') check();
    });
    return () => sub.remove();
  }, [check]);

  const snooze = useCallback(() => {
    setInstallUrl(null);
    AsyncStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS)).catch(() => {});
  }, []);

  const update = useCallback(() => {
    if (installUrl) Linking.openURL(installUrl).catch(() => {});
  }, [installUrl]);

  return { visible: installUrl !== null, snooze, update };
}
